import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { RepeatableRowFormFactory } from '../field/repeatable-group/logic/repeatable-row-form.factory';
import type { FormField } from '../interfaces/form.interface';
import { FormType } from '../interfaces/form.interface';
import { InputType } from '../interfaces/input.interface';
import { ReactiveFormDefaultValueContext, ReactiveFormDefaultValueResolver } from './reactive-form-default-value.resolver';
import { ReactiveFormValidatorsFactory } from './reactive-form-validators.factory';
import { ReactiveFormValueUtils } from './reactive-form-value.utils';

export type ReactiveFormModelFactoryContext<T> = ReactiveFormDefaultValueContext<T>;

export class ReactiveFormModelFactory<T> {
    private readonly defaultValueResolver = new ReactiveFormDefaultValueResolver<T>();
    private readonly validatorsFactory = new ReactiveFormValidatorsFactory<T>();

    public create(context: ReactiveFormModelFactoryContext<T>): FormGroup {
        const controls = context.form.sections
            .filter((section) => section.isVisible)
            .flatMap((section) => section.fields)
            .filter((field) => this.shouldCreateDynamicControl(field))
            .reduce((group, field) => this.appendFieldControls(group, field, context), {} as Record<string, AbstractControl>);

        return new FormGroup(controls);
    }

    private appendFieldControls(
        group: Record<string, AbstractControl>,
        field: FormField<T>,
        context: ReactiveFormModelFactoryContext<T>,
    ): Record<string, AbstractControl> {
        if (!this.shouldCreateControl(field)) {
            return group;
        }

        if (ReactiveFormValueUtils.isRepeatableField(field)) {
            return this.appendRepeatableControls(group, field, context);
        }

        if (field.input?.type === InputType.TWO_FIELDS_CONCAT) {
            return this.appendTwoFieldsConcatControls(group, field, context);
        }

        if (group[field.name]) {
            return group;
        }

        group[field.name] = new FormControl(
            this.defaultValueResolver.resolve(field, context),
            this.validatorsFactory.create(field),
        );

        return group;
    }

    private shouldCreateDynamicControl(field: FormField<T>): boolean {
        const shouldCreateHiddenControl = field.input?.type === InputType.HIDDEN;

        return (
            field.conditions.isVisible ||
            shouldCreateHiddenControl ||
            this.hasRuntimeVisibility(field)
        ) && !field.conditions.isRetained;
    }

    private hasRuntimeVisibility(field: FormField<T>): boolean {
        return Boolean(
            field.turnVisible ||
            field.visibleWhen !== undefined ||
            field.readonlyWhen !== undefined ||
            field.disabledWhen !== undefined,
        );
    }

    private shouldCreateControl(field: FormField<T>): field is FormField<T> & { name: string } {
        return Boolean(field.name?.trim()) &&
            field.input?.type !== InputType.EXTERNAL &&
            (field.input?.type !== InputType.EMPTY || Boolean(field.input?.dynamicTypeConfiguration));
    }

    private appendRepeatableControls(
        group: Record<string, AbstractControl>,
        field: FormField<T>,
        context: ReactiveFormModelFactoryContext<T>,
    ): Record<string, AbstractControl> {
        const fieldName = field.name ?? '';

        if (fieldName && !group[fieldName]) {
            group[fieldName] = RepeatableRowFormFactory.createRowsArray(
                field.input?.repeatableGroupConfiguration?.subFields ?? [],
                this.validatorsFactory.create(field),
            );
        }

        const bindings = field.input?.repeatableGroupConfiguration?.bindings ?? {};
        const previousData = (context.previousData as Record<string, unknown>) ?? {};

        for (const targetControlName of Object.values(bindings)) {
            const controlName = String(targetControlName ?? '');

            if (!controlName) {
                continue;
            }

            const shouldForget = context.form.type === FormType.DUPLICATION && field.conditions.isRememberedOnDuplicate === false;
            const previousRawValue = shouldForget ? '' : (previousData[controlName] ?? '');

            if (!group[controlName]) {
                group[controlName] = new FormControl(String(previousRawValue ?? ''), []);
            }
        }

        return group;
    }

    private appendTwoFieldsConcatControls(
        group: Record<string, AbstractControl>,
        field: FormField<T>,
        context: ReactiveFormModelFactoryContext<T>,
    ): Record<string, AbstractControl> {
        const config = field.input.twoFieldsConcatConfiguration;
        const subField1 = config?.subField1 ?? '';
        const subField2 = config?.subField2 ?? '';
        const separator = config?.separator ?? 'x';
        const parentDefaultRaw = this.defaultValueResolver.resolve(field, context);
        const parentDefault = String(parentDefaultRaw ?? '').trim();
        const [subField1Default, subField2Default] = ReactiveFormValueUtils.splitTwoFieldsConcat(parentDefault, separator);
        const validators = this.validatorsFactory.create(field);

        const parentControlName = field.name ?? '';

        if (parentControlName && !group[parentControlName]) {
            group[parentControlName] = new FormControl(parentDefault, validators);
        }

        if (subField1 && !group[subField1]) {
            group[subField1] = new FormControl(subField1Default, validators);
        }

        if (subField2 && !group[subField2]) {
            group[subField2] = new FormControl(subField2Default, validators);
        }

        return group;
    }
}
